import type { MouseEvent } from "react";
import { ErrorBanner } from "@/components/ErrorBanner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";

interface ConfirmDialogProps {
  /**
   * Blocks the confirm action while the dialog stays open, for a decision the
   * caller has to explain (via `errorMessage`) before it can be taken.
   */
  confirmDisabled?: boolean;
  confirmLabel?: string;
  description: string;
  errorMessage?: string | null;
  loading?: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  title: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  confirmDisabled = false,
  onConfirm,
  loading = false,
  errorMessage,
}: ConfirmDialogProps) {
  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {errorMessage && <ErrorBanner message={errorMessage} />}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={loading || confirmDisabled}
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {loading && <Spinner />}
            {loading ? `${confirmLabel}...` : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
